export const JARVIS_SPREADSHEET_BRIDGE = 'jarvis-spreadsheet-bridge';

export interface SpreadsheetCellRange {
  leftCol: number;
  topRow: number;
  rightCol: number;
  bottomRow: number;
}

export interface SpreadsheetSelectionState {
  sheetIndex: number;
  sheetName: string;
  ranges: SpreadsheetCellRange[];
  activeCell: { col: number; row: number };
  selectedText: string;
  rangeLabel: string;
}

export interface SpreadsheetNavigateAnchorPayload {
  sheetIndex: number;
  sheetName?: string;
  /** A1-style bookmark, e.g. Sheet1!G22:G26 — preferred by the DevExpress host. */
  bookmark?: string;
  ranges: SpreadsheetCellRange[];
  anchorId?: string;
}

/** Coerce API/DB range JSON (camelCase or PascalCase) into bridge coordinates. */
export function normalizeSpreadsheetRanges(
  ranges: readonly (SpreadsheetCellRange | Record<string, unknown>)[] | null | undefined
): SpreadsheetCellRange[] {
  if (!ranges?.length) return [];
  const out: SpreadsheetCellRange[] = [];
  for (const raw of ranges) {
    const r = raw as Record<string, unknown>;
    const leftCol = Number(r.leftCol ?? r.LeftCol);
    const topRow = Number(r.topRow ?? r.TopRow);
    const rightCol = Number(r.rightCol ?? r.RightCol);
    const bottomRow = Number(r.bottomRow ?? r.BottomRow);
    if (
      !Number.isFinite(leftCol) ||
      !Number.isFinite(topRow) ||
      !Number.isFinite(rightCol) ||
      !Number.isFinite(bottomRow)
    ) {
      continue;
    }
    out.push({ leftCol, topRow, rightCol, bottomRow });
  }
  return out;
}

export function spreadsheetAnchorBookmark(anchor: {
  sheetName?: string | null;
  selectedText?: string | null;
  ranges?: readonly (SpreadsheetCellRange | Record<string, unknown>)[] | null;
}): string | null {
  const text = anchor.selectedText?.trim();
  if (text && /![A-Za-z]+\d+/.test(text)) return text;
  const ranges = normalizeSpreadsheetRanges(anchor.ranges);
  if (!ranges.length) return text || null;
  return formatRangeLabel(anchor.sheetName?.trim() ?? '', ranges);
}

/** Auto-save status reported by the host page to the shell. */
export type AutoSaveBridgeStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'error' | 'offline';

/** Messages posted from the spreadsheet iframe to the parent shell. */
export type SpreadsheetBridgeMessage =
  | { type: 'jarvis-spreadsheet:ready'; hostOrigin?: string; sessionId?: string | null }
  | { type: 'jarvis-spreadsheet:open-failed'; message?: string | null }
  | { type: 'jarvis-spreadsheet:selection-changed'; selection: SpreadsheetSelectionState }
  | { type: 'jarvis-spreadsheet:navigation-complete'; ok: boolean; rangeLabel?: string }
  | {
      type: 'jarvis-spreadsheet:request-comment-on-selection';
      selection?: SpreadsheetSelectionState | null;
    }
  | { type: 'jarvis-spreadsheet:request-toggle-cell-links' }
  | { type: 'jarvis-spreadsheet:request-toggle-discussion' }
  | { type: 'jarvis-spreadsheet:request-share' }
  | { type: 'jarvis-spreadsheet:request-open'; source: 'pc' | 'storage' }
  | { type: 'jarvis-spreadsheet:request-set-discussion-placement'; placement: 'left' | 'right' | 'off' }
  | { type: 'jarvis-spreadsheet:dirty-state'; dirty: boolean }
  | {
      type: 'jarvis-spreadsheet:auto-save-state';
      status: AutoSaveBridgeStatus;
      lastSavedAtUtc: number | null;
      reason?: string | null;
    }
  | {
      type: 'jarvis-spreadsheet:auto-save-complete';
      requestId: string;
      ok: boolean;
      reason?: string | null;
    }
  | { type: 'jarvis-spreadsheet:editing-changed'; editing: boolean }
  | { type: 'jarvis-spreadsheet:typing-activity' };

/** Messages posted from the parent shell into the spreadsheet iframe. */
export type SpreadsheetHostMessage =
  | { type: 'jarvis-spreadsheet:navigate-anchor'; payload: SpreadsheetNavigateAnchorPayload }
  | { type: 'jarvis-spreadsheet:cancel-navigation' }
  | { type: 'jarvis-spreadsheet:set-discussion-active'; active: boolean }
  | { type: 'jarvis-spreadsheet:cell-links-state'; on: boolean }
  | { type: 'jarvis-spreadsheet:discussion-state'; on: boolean }
  | { type: 'jarvis-spreadsheet:discussion-placement-state'; placement: 'left' | 'right' | 'off' }
  | { type: 'jarvis-spreadsheet:request-dirty-state' }
  | { type: 'jarvis-spreadsheet:request-force-reload'; overlay?: boolean }
  | { type: 'jarvis-spreadsheet:request-auto-save-now'; requestId?: string }
  | { type: 'jarvis-spreadsheet:set-peer-edit-locked'; locked: boolean }
  | {
      type: 'jarvis-spreadsheet:set-autosave-config';
      enabled: boolean;
      intervalSeconds: number;
    };

export function colToLetters(index: number): string {
  let s = '';
  let n = index | 0;
  do {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return s;
}

export function cellRef(col: number, row: number): string {
  return `${colToLetters(col)}${row + 1}`;
}

export function formatRangeLabel(sheetName: string, ranges: SpreadsheetCellRange[]): string {
  if (!ranges.length) return sheetName || 'Selection';
  const r = ranges[0];
  const a = cellRef(r.leftCol, r.topRow);
  const b = cellRef(r.rightCol, r.bottomRow);
  const range = a === b ? a : `${a}:${b}`;
  return sheetName ? `${sheetName}!${range}` : range;
}
